import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BookOpen, FileText, Download, Mail, MessageSquare, Send, Copy, Phone, History } from "lucide-react";
import { api, API, fmtINR, fmtDate, apiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import { canWrite } from "../lib/permissions";

export default function PartyLedger() {
  const { type: urlType, id: urlId } = useParams();
  const navigate = useNavigate();
  const [type, setType] = useState(urlType === "supplier" ? "supplier" : "customer");
  const [partyId, setPartyId] = useState(urlId || "");
  const [parties, setParties] = useState({ customers: [], suppliers: [] });
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [d, setD] = useState(null);
  const { user } = useAuth();
  const canRemind = canWrite(user, "payments");
  const [rem, setRem] = useState(null);
  const [remOpen, setRemOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [bulk, setBulk] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [queue, setQueue] = useState(null);
  const [done, setDone] = useState([]);
  const [numOpen, setNumOpen] = useState(false);
  const [numRows, setNumRows] = useState([]);
  const [nums, setNums] = useState({});
  const [savingNums, setSavingNums] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [hist, setHist] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [share, setShare] = useState(null);

  const loadReminder = (id = partyId) => api.get(`/ledger/customer/${id}/reminder-preview`)
    .then((r) => setRem(r.data)).catch(() => setRem(null));

  const sendReminder = async () => {
    setSending(true);
    try {
      const r = await api.post(`/ledger/customer/${partyId}/reminder`);
      toast.success(`Reminder emailed to ${r.data.email}`);
      setRemOpen(false); loadReminder();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSending(false);
  };

  const sendBulk = async () => {
    if (!window.confirm("Email a payment reminder to every customer with an outstanding balance?")) return;
    setSending(true);
    try {
      const r = await api.post("/ledger/reminders/bulk");
      setBulk(r.data); setBulkOpen(true);
      toast.success(`${r.data.sent_count} reminder(s) sent`);
      if (partyId && type === "customer") loadReminder();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSending(false);
  };

  const copyText = async () => {
    try { await navigator.clipboard.writeText(rem?.whatsapp_text || ""); toast.success("Message copied — paste it into WhatsApp"); }
    catch { toast.error("Could not copy, please select the text manually"); }
  };

  const sendWhatsApp = () => {
    if (!rem?.whatsapp_link) { toast.error("This customer has no mobile/WhatsApp number on record"); return; }
    window.open(rem.whatsapp_link, "_blank");
  };

  const openQueue = async () => {
    setQueueOpen(true);
    setDone([]);
    try { const { data } = await api.get("/payments/whatsapp-queue"); setQueue(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openNumbers = async () => {
    setNumOpen(true);
    setNums({});
    try { const { data } = await api.get("/customers/missing-mobile"); setNumRows(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveNumbers = async () => {
    const numbers = Object.entries(nums).filter(([, v]) => String(v).replace(/\D/g, "").length >= 10)
      .map(([customer_id, mobile]) => ({ customer_id, mobile: String(mobile) }));
    if (!numbers.length) return toast.error("Enter at least one valid 10-digit number");
    setSavingNums(true);
    try {
      const { data } = await api.post("/customers/mobiles", { numbers });
      toast.success(data.message);
      setNumOpen(false);
      if (queueOpen) openQueue();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSavingNums(false);
  };

  const openHistory = async () => {
    setHistOpen(true);
    try { const { data } = await api.get("/payments/reminder-history?days=90"); setHist(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openShare = async () => {
    setShareOpen(true);
    setShare(null);
    try {
      const { data } = await api.get(`/ledger/customer/${partyId}/share?start=${start}&end=${end}`);
      setShare(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const shareOn = (link, url) => {
    if (link) { window.open(link, "_blank"); return; }
    if (!share?.mobile) { toast.error("This customer has no mobile/WhatsApp number — add it from the reminder queue"); }
    if (url) window.open(url, "_blank");
  };

  const queueSend = async (row) => {
    window.open(row.whatsapp_link, "_blank");
    setDone((p) => [...p, row.customer_id]);
    try { await api.post(`/ledger/customer/${row.customer_id}/reminder-mark`); } catch { /* keep going */ }
  };

  useEffect(() => { api.get("/ledger/parties").then((r) => setParties(r.data)).catch(() => {}); }, []);

  const load = (t = type, id = partyId, st = start, en = end) => {
    if (!id) { setD(null); return; }
    api.get(`/ledger/${t}/${id}?start=${st}&end=${en}`).then((r) => setD(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  };
  useEffect(() => { load(); if (type === "customer" && partyId) loadReminder(partyId); else setRem(null); /* eslint-disable-next-line */ }, [type, partyId]);

  const switchType = (t) => { setType(t); setPartyId(""); setD(null); navigate(`/ledger/${t}`); };
  const pickParty = (id) => { setPartyId(id); navigate(`/ledger/${type}/${id}`); };

  const list = type === "customer" ? parties.customers : parties.suppliers;
  const qs = `token=${localStorage.getItem("token")}&start=${start}&end=${end}`;
  const isCust = type === "customer";

  return (
    <div data-testid="module-ledger">
      <div className="mb-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-[#0B132B]" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Party Ledger</h1>
            <p className="text-sm text-slate-500 mt-0.5">Full billing &amp; payment history with a running balance</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isCust && canRemind && (
            <>
              <Button data-testid="ledger-whatsapp-queue" size="sm" className="rounded-sm gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openQueue}><MessageSquare className="w-4 h-4" /> WhatsApp All Overdue</Button>
              <Button data-testid="ledger-reminder-history" size="sm" variant="outline" className="rounded-sm gap-1.5 border-slate-300" onClick={openHistory}><History className="w-4 h-4" /> Reminder History</Button>
              <Button data-testid="ledger-bulk-reminder" size="sm" variant="outline" className="rounded-sm gap-1.5 border-slate-300" disabled={sending} onClick={sendBulk}><Send className="w-4 h-4" /> Remind All Overdue</Button>
            </>
          )}
          {partyId && isCust && canRemind && (
            <>
              <Button data-testid="ledger-share-whatsapp" size="sm" className="rounded-sm gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openShare}><MessageSquare className="w-4 h-4" /> Share on WhatsApp</Button>
              <Button data-testid="ledger-send-reminder" size="sm" className="rounded-sm gap-1.5 bg-[#0B132B] hover:bg-[#1C2541] text-white" onClick={() => { loadReminder(); setRemOpen(true); }}><Mail className="w-4 h-4" /> Send Reminder</Button>
            </>
          )}
          {partyId && (
            <>
              <Button data-testid="ledger-export-pdf" size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => window.open(`${API}/ledger/${type}/${partyId}/pdf?${qs}`, "_blank")}><FileText className="w-4 h-4" /> PDF</Button>
              <Button data-testid="ledger-export-excel" size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => window.open(`${API}/ledger/${type}/${partyId}/xlsx?${qs}`, "_blank")}><Download className="w-4 h-4" /> Excel</Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-end gap-3 mb-5">
        <Tabs value={type} onValueChange={switchType}>
          <TabsList>
            <TabsTrigger value="customer" data-testid="ledger-tab-customer">Customers</TabsTrigger>
            <TabsTrigger value="supplier" data-testid="ledger-tab-supplier">Suppliers</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="space-y-1 w-full sm:w-80">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{isCust ? "Customer" : "Supplier"}</label>
          <Select value={partyId} onValueChange={pickParty}>
            <SelectTrigger data-testid="ledger-party-select" className="bg-white h-9"><SelectValue placeholder={`Select a ${type}`} /></SelectTrigger>
            <SelectContent>{list.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">From</label>
          <Input data-testid="ledger-start-date" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-white h-9 w-40" /></div>
        <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">To</label>
          <Input data-testid="ledger-end-date" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-white h-9 w-40" /></div>
        <Button data-testid="ledger-apply-filter" onClick={() => load()} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-md h-9">Apply</Button>
      </div>

      {!partyId ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg py-20 text-center text-slate-400" data-testid="ledger-empty-state">
          Select a {type} above to view their ledger
        </div>
      ) : !d ? (
        <div className="py-20 text-center text-slate-400">Loading...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
            <div className="bg-[#0B132B] text-white rounded-lg p-5 shadow-sm" data-testid="ledger-closing-balance-card">
              <div className="text-xs font-semibold uppercase tracking-wider text-white/60">{isCust ? "Outstanding (Receivable)" : "Outstanding (Payable)"}</div>
              <div className="text-2xl font-mono font-semibold tabular-nums mt-2">{fmtINR(d.closing_balance)}</div>
              <div className="text-xs text-white/50 mt-1">{d.party_name}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="ledger-total-debit">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{isCust ? "Total Billed (Dr)" : "Total Purchases (Dr)"}</div>
              <div className="text-2xl font-mono font-semibold tabular-nums text-slate-900 mt-2">{fmtINR(d.total_debit)}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="ledger-total-credit">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{isCust ? "Total Received (Cr)" : "Total Paid (Cr)"}</div>
              <div className="text-2xl font-mono font-semibold tabular-nums text-emerald-600 mt-2">{fmtINR(d.total_credit)}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="ledger-opening-balance-card">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Opening Balance</div>
              <div className="text-2xl font-mono font-semibold tabular-nums text-slate-900 mt-2">{fmtINR(d.opening_balance)}</div>
              <div className="text-xs text-slate-400 mt-1">{d.party_meta || "—"}</div>
              {isCust && rem?.last_reminded_at && (
                <div className="text-xs text-indigo-600 mt-1" data-testid="ledger-last-reminded">Last reminded {fmtDate(rem.last_reminded_at)}{rem.last_reminded_by ? ` by ${rem.last_reminded_by}` : ""}</div>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="ledger-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
                {["Date", "Particulars", "Doc No", "Mode", "Debit", "Credit", "Balance"].map((h, i) => (
                  <th key={h} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap ${i >= 4 ? "text-right" : ""}`}>{h}</th>))}
              </tr></thead>
              <tbody>
                <tr className="bg-slate-50/70 border-b border-slate-200">
                  <td className="px-4 py-3 text-slate-500" colSpan={6}>Opening Balance</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold" data-testid="ledger-opening-row">{fmtINR(d.opening_balance)}</td>
                </tr>
                {d.rows.length === 0 ? <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No transactions in this period</td></tr>
                  : d.rows.map((r, i) => (
                    <tr key={`${r.source}-${r.doc_id}-${i}`} data-testid={`ledger-row-${r.doc_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 whitespace-nowrap text-slate-600">{r.date ? fmtDate(r.date) : "-"}</td>
                      <td className="px-4 py-3 text-slate-800">
                        <Badge variant="outline" className={`rounded-sm text-[10px] mr-2 ${r.source === "payment" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>{r.kind}</Badge>
                        {r.description !== "-" && <span className="text-xs text-slate-400">{r.description}</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{r.doc_no}</td>
                      <td className="px-4 py-3 text-slate-600">{r.mode}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-slate-900">{r.debit ? fmtINR(r.debit) : ""}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-emerald-600">{r.credit ? fmtINR(r.credit) : ""}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold">{fmtINR(r.balance)}</td>
                    </tr>))}
                <tr className="bg-slate-50 font-semibold border-t-2 border-slate-200">
                  <td className="px-4 py-3" colSpan={4}>TOTAL / CLOSING BALANCE</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{fmtINR(d.total_debit)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-emerald-600">{fmtINR(d.total_credit)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums" data-testid="ledger-closing-row">{fmtINR(d.closing_balance)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={remOpen} onOpenChange={setRemOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-head">Send Payment Reminder</DialogTitle>
            <DialogDescription>A polite reminder email with the outstanding amount, pending invoices and your bank details.</DialogDescription></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-slate-100 pb-2"><span className="text-slate-500">Customer</span><span className="font-medium text-slate-800">{rem?.customer_name || d?.party_name}</span></div>
            <div className="flex justify-between border-b border-slate-100 pb-2"><span className="text-slate-500">Email</span><span data-testid="reminder-email" className={rem?.email ? "font-mono text-slate-800" : "text-red-600"}>{rem?.email || "No email on record"}</span></div>
            <div className="flex justify-between border-b border-slate-100 pb-2"><span className="text-slate-500">WhatsApp / Mobile</span><span data-testid="reminder-mobile" className={rem?.mobile ? "font-mono text-slate-800" : "text-red-600"}>{rem?.mobile || "No number on record"}</span></div>
            <div className="flex justify-between border-b border-slate-100 pb-2"><span className="text-slate-500">Outstanding</span><span className="font-mono font-semibold text-red-600">{fmtINR(rem?.outstanding || 0)}</span></div>
            <div className="flex justify-between border-b border-slate-100 pb-2"><span className="text-slate-500">Pending invoices</span><span className="font-mono text-slate-800">{rem?.pending_count ?? 0}</span></div>
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> WhatsApp message (includes bill PDF link)</label>
              <Textarea data-testid="reminder-whatsapp-text" readOnly value={rem?.whatsapp_text || ""} className="bg-slate-50 font-mono text-xs h-40" />
              <Button data-testid="reminder-copy-text" size="sm" variant="outline" className="rounded-sm gap-1.5 mt-1" onClick={copyText}><Copy className="w-3.5 h-3.5" /> Copy message</Button>
              {rem?.company_whatsapp && <p className="text-xs text-slate-400 mt-1">Customers are asked to reply on your company WhatsApp: +{rem.company_whatsapp} (change it in Company Settings)</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemOpen(false)} className="rounded-sm">Close</Button>
            <Button data-testid="reminder-send-whatsapp" disabled={!rem?.mobile || !(rem?.outstanding > 0)} onClick={sendWhatsApp} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1.5"><MessageSquare className="w-4 h-4" /> Send on WhatsApp</Button>
            <Button data-testid="reminder-send-email" disabled={!rem?.email || sending || !(rem?.outstanding > 0)} onClick={sendReminder} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><Mail className="w-4 h-4" /> {sending ? "Sending..." : "Send Email"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={queueOpen} onOpenChange={setQueueOpen}>
        <DialogContent className="max-w-2xl" data-testid="whatsapp-queue-dialog">
          <DialogHeader><DialogTitle className="font-head">WhatsApp reminders — one tap at a time</DialogTitle>
            <DialogDescription>
              {queue ? `${queue.rows?.length || 0} customer(s) owe ${fmtINR(queue.total_outstanding || 0)}. Tap "Send" to open WhatsApp with the message and bill link ready, press send there, then come back for the next one.` : "Loading overdue customers..."}
            </DialogDescription></DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
            {!queue?.rows?.length ? <div className="px-4 py-10 text-center text-slate-400" data-testid="whatsapp-queue-empty">No overdue customer with a mobile number</div>
              : queue.rows.map((r) => (
                <div key={r.customer_id} data-testid={`whatsapp-queue-row-${r.customer_id}`} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 truncate">{r.customer_name || "(unnamed customer)"}</div>
                    <div className="text-xs text-slate-400">{r.mobile} · {r.pending_count} pending{r.last_reminded_at ? ` · last reminded ${fmtDate(r.last_reminded_at)}` : ""}</div>
                    {r.reminded_this_week && <div className="text-xs text-amber-700 font-medium" data-testid={`whatsapp-queue-recent-${r.customer_id}`}>Already reminded this week</div>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-mono tabular-nums font-semibold text-red-600">{fmtINR(r.outstanding)}</span>
                    <Button data-testid={`whatsapp-queue-send-${r.customer_id}`} size="sm" onClick={() => queueSend(r)}
                      className={`rounded-sm gap-1.5 ${done.includes(r.customer_id) ? "bg-slate-400 hover:bg-slate-500" : "bg-emerald-600 hover:bg-emerald-700"} text-white`}>
                      <MessageSquare className="w-3.5 h-3.5" /> {done.includes(r.customer_id) ? "Sent · again" : "Send"}
                    </Button>
                  </div>
                </div>))}
          </div>
          {queue?.skipped?.length > 0 && (
            <p className="text-xs text-amber-700" data-testid="whatsapp-queue-skipped">
              {queue.skipped.length} customer(s) skipped — no mobile number on record: {queue.skipped.map((s) => s.customer_name).join(", ")}
            </p>
          )}
          <DialogFooter>
            <Button data-testid="queue-add-numbers" variant="outline" className="rounded-sm gap-1.5" onClick={openNumbers}><Phone className="w-4 h-4" /> Add missing numbers</Button>
            <Button variant="outline" className="rounded-sm" onClick={() => setQueueOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={numOpen} onOpenChange={setNumOpen}>
        <DialogContent className="max-w-2xl" data-testid="missing-mobile-dialog">
          <DialogHeader><DialogTitle className="font-head">Customers without a mobile number</DialogTitle>
            <DialogDescription>Add their WhatsApp/mobile number (10 digits) and save — they will then appear in the WhatsApp reminder queue.</DialogDescription></DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto border border-slate-200 rounded-md">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-slate-200 sticky top-0">
                {["Customer", "City", "Outstanding", "Mobile / WhatsApp"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>))}
              </tr></thead>
              <tbody>
                {!numRows.length ? <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400" data-testid="missing-mobile-empty">Every customer has a number saved</td></tr>
                  : numRows.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100" data-testid={`missing-mobile-row-${c.id}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{c.company_name || "(unnamed)"}<div className="text-[10px] text-slate-400">{c.contact_person || "-"}</div></td>
                      <td className="px-3 py-2 text-slate-500">{c.city || "-"}</td>
                      <td className="px-3 py-2 font-mono tabular-nums text-red-600">{c.outstanding > 0 ? fmtINR(c.outstanding) : "-"}</td>
                      <td className="px-3 py-2">
                        <Input data-testid={`missing-mobile-input-${c.id}`} inputMode="numeric" placeholder="9812345678" className="h-8 w-40"
                          value={nums[c.id] ?? ""} onChange={(e) => setNums((p) => ({ ...p, [c.id]: e.target.value }))} />
                      </td>
                    </tr>))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setNumOpen(false)}>Close</Button>
            <Button data-testid="missing-mobile-save" disabled={savingNums || !numRows.length} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm" onClick={saveNumbers}>
              {savingNums ? "Saving..." : "Save Numbers"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-xl" data-testid="ledger-share-dialog">
          <DialogHeader><DialogTitle className="font-head">Share on WhatsApp</DialogTitle>
            <DialogDescription>
              {share ? <>Send <b>{share.customer_name || "this customer"}</b> {share.mobile ? <>on <span className="font-mono">{share.mobile}</span></> : <span className="text-red-600">— no mobile number on record</span>} · Outstanding {fmtINR(share.outstanding || 0)}</> : "Preparing links..."}
            </DialogDescription></DialogHeader>

          <div className="rounded-md border border-slate-200 p-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-slate-800">Full ledger statement</div>
              <div className="text-xs text-slate-400">All bills, payments and running balance for the selected date range</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button data-testid="share-statement-pdf" size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => window.open(share?.statement_pdf_url, "_blank")}><FileText className="w-3.5 h-3.5" /> Preview</Button>
              <Button data-testid="share-statement-whatsapp" size="sm" disabled={!share?.statement_whatsapp_link} className="rounded-sm gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => shareOn(share?.statement_whatsapp_link, share?.statement_pdf_url)}><MessageSquare className="w-3.5 h-3.5" /> Send</Button>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Or send a single bill</div>
            <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
              {!share?.bills?.length ? <div className="px-3 py-8 text-center text-slate-400 text-sm" data-testid="share-no-bills">No pending bills for this customer</div>
                : share.bills.map((b) => (
                  <div key={b.invoice_id} data-testid={`share-bill-${b.invoice_id}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 truncate">{b.invoice_no}</div>
                      <div className="text-xs text-slate-400">{fmtDate(b.date)} · due {fmtINR(b.outstanding)}</div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button data-testid={`share-bill-pdf-${b.invoice_id}`} size="sm" variant="outline" className="rounded-sm h-8" onClick={() => window.open(b.pdf_url, "_blank")}>Preview</Button>
                      <Button data-testid={`share-bill-whatsapp-${b.invoice_id}`} size="sm" className="rounded-sm h-8 gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={async () => {
                          try {
                            const { data } = await api.get(`/invoices/${b.invoice_id}/share`);
                            if (!data.whatsapp_link) { toast.error("No mobile number on record for this customer"); return; }
                            window.open(data.whatsapp_link, "_blank");
                          } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
                        }}><MessageSquare className="w-3.5 h-3.5" /> Send</Button>
                    </div>
                  </div>))}
            </div>
          </div>
          {share?.company_whatsapp && <p className="text-xs text-slate-400">Customers are asked to reply on +{share.company_whatsapp} (change it in Company Settings)</p>}
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setShareOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-3xl" data-testid="reminder-history-dialog">
          <DialogHeader><DialogTitle className="font-head">Reminder history — last 90 days</DialogTitle>
            <DialogDescription>
              {hist ? `${hist.total} reminder(s) sent · ${hist.reminded_last_7_days} customer(s) already reminded in the last 7 days` : "Loading..."}
            </DialogDescription></DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto border border-slate-200 rounded-md">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-slate-200 sticky top-0">
                {["Sent On", "Customer", "Channel", "Sent To", "Outstanding Then", "By"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>))}
              </tr></thead>
              <tbody>
                {!hist?.rows?.length ? <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400" data-testid="reminder-history-empty">No reminders sent yet</td></tr>
                  : hist.rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100" data-testid={`reminder-history-row-${i}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-700">{fmtDate(r.sent_at)}</td>
                      <td className="px-3 py-2 font-medium text-slate-800">{r.customer_name || "-"}</td>
                      <td className="px-3 py-2"><span className={`text-xs font-medium ${r.channel === "whatsapp" ? "text-emerald-700" : "text-slate-600"}`}>{r.channel === "whatsapp" ? "WhatsApp" : "Email"}</span></td>
                      <td className="px-3 py-2 text-slate-500 truncate max-w-[220px]">{r.channel === "whatsapp" ? (r.mobile || "-") : (r.email || "-")}</td>
                      <td className="px-3 py-2 font-mono tabular-nums text-red-600">{fmtINR(r.outstanding)}</td>
                      <td className="px-3 py-2 text-slate-500">{r.sent_by || "-"}</td>
                    </tr>))}
              </tbody>
            </table>
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setHistOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-head">Reminders Sent</DialogTitle>
            <DialogDescription>Every customer with an outstanding balance and an email on record.</DialogDescription></DialogHeader>
          <div className="space-y-3 text-sm max-h-80 overflow-y-auto" data-testid="bulk-reminder-result">
            <div className="font-semibold text-slate-800">{bulk?.sent_count || 0} reminder(s) emailed</div>
            {(bulk?.sent || []).map((s, i) => (
              <div key={i} className="flex justify-between border-b border-slate-100 pb-1">
                <span className="text-slate-700">{s.customer_name}</span>
                <span className="font-mono text-xs text-slate-500">{s.email} · {fmtINR(s.outstanding)}</span>
              </div>))}
            {(bulk?.skipped || []).length > 0 && <div className="pt-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Skipped</div>}
            {(bulk?.skipped || []).map((s, i) => (
              <div key={`s-${i}`} className="flex justify-between border-b border-slate-100 pb-1">
                <span className="text-slate-700">{s.customer_name}</span>
                <span className="text-xs text-amber-600">{s.reason}</span>
              </div>))}
          </div>
          <DialogFooter><Button onClick={() => setBulkOpen(false)} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
